import React, { useState, useEffect } from 'react';
import { QrCode, MapPin } from 'lucide-react';
import Popup from '../components/Popup';
import { apiUrl } from '../utils/apiUrl';
import './Orchard.css';

function Orchard() {
  const totalPitstops = 7;
  const [checkedInPitstops, setCheckedInPitstops] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '' });
  const hasBadge = checkedInPitstops > 0;

  useEffect(() => {
    // Reset progress every time user enters this page
    setCheckedInPitstops(0);
  }, []);

  const handleScan = () => {
    setIsScanning(true);
    
    // Simulate scanning delay
    setTimeout(async () => {
      try {
        const response = await fetch(apiUrl('/api/durian/checkin'), { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location: 'Musang King Tree #1' })
        });
        const data = await response.json();
        
        if (data.status === 'Success') {
          completeCheckin();
        }
      } catch (error) {
        // Fallback for demo
        completeCheckin();
      }
    }, 1500);
  };

  const completeCheckin = () => {
    localStorage.setItem('durian_badge_earned', 'true');
    setCheckedInPitstops((prev) => Math.min(prev + 1, totalPitstops));
    setIsScanning(false);
    setPopup({ 
      isOpen: true, 
      title: 'Badge Unlocked!', 
      message: "Great! You've collected a new badge. Please continue to explore the next pitstop and collecting more badges. Your last pitstop will bring you to the Tasting Pavilion to unlock your hidden rewards!",
      icon: <img src="/durian-badge.png" alt="Badge" style={{ width: '140px', height: '140px', objectFit: 'contain', filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))' }} />
    });
  };

  return (
    <div className="page-container orchard-page">
      <div className="header">
        <h2>The Orchard Trails</h2>
        <p>Find the legendary trees and collect badges.</p>
      </div>

      <div className="map-container">
        <svg className="orchard-svg-map" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">
          <path d="M 0,150 C 100,150 150,50 250,100 C 350,150 350,250 400,250" fill="none" stroke="#A7F3D0" strokeWidth="24" strokeLinecap="round" />
          <path d="M 0,150 C 100,150 150,50 250,100 C 350,150 350,250 400,250" fill="none" stroke="#FDE68A" strokeWidth="4" strokeDasharray="10,10" />
          
          {/* Decorative Trees */}
          <circle cx="80" cy="80" r="18" fill="#059669" />
          <circle cx="95" cy="95" r="15" fill="#10B981" />
          <circle cx="65" cy="95" r="14" fill="#34D399" />

          <circle cx="320" cy="80" r="22" fill="#059669" />
          <circle cx="295" cy="100" r="18" fill="#10B981" />
          <circle cx="340" cy="100" r="16" fill="#34D399" />
          
          <circle cx="180" cy="220" r="20" fill="#059669" />
          <circle cx="160" cy="235" r="16" fill="#10B981" />
          <circle cx="200" cy="235" r="14" fill="#34D399" />
        </svg>
        
        <div className="tree-marker marker-1">
          <div className="marker-pin"><MapPin size={24} color="white" /></div>
          <span>Musang King #1</span>
        </div>
        <div className="tree-marker marker-2">
          <div className="marker-pin gold"><MapPin size={24} color="white" /></div>
          <span>Black Thorn</span>
        </div>
      </div>

      <div className="card pitstop-showcase">
        <div className="pitstop-showcase-header">
          <h3>Pitstop Progress</h3>
          <p>{checkedInPitstops}/{totalPitstops} Checked-in</p>
        </div>
        <div className="pitstop-track">
          {Array.from({ length: totalPitstops }, (_, index) => {
            const pitstopNumber = index + 1;
            const isCheckedIn = pitstopNumber <= checkedInPitstops;
            return (
              <div key={pitstopNumber} className={`pitstop-item ${isCheckedIn ? 'checked-in' : ''}`}>
                <div className="pitstop-icon">{pitstopNumber}</div>
                <span>Pitstop {pitstopNumber}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="action-section">
        <div className="card scan-card">
          <h3>{hasBadge ? 'Keep Exploring!' : 'Checkpoint 1 : Musang King'}</h3>
          <p>Rich, creamy, and perfectly bittersweet with a smooth melt-in-your-mouth texture.</p>
          <p>Scan the QR code on the tree to claim rewards.</p>
          <button 
            className="btn-primary" 
            onClick={handleScan}
            disabled={isScanning}
          >
            <QrCode size={20} />
            {isScanning ? 'Scanning...' : 'Scan Tree QR'}
          </button>
        </div>

        {hasBadge && (
          <div className="card badge-card">
            <div className="badge-icon">
              <img src="/durian-badge.png" alt="Musang King Explorer Badge" className="earned-badge-img" />
            </div>
            <h3 className="badge-title">Musang King Explorer</h3>
            <p>Badge Earned!</p>
            <p className="hint">Go to the Tasting Pavilion to claim your mystery gift.</p>
          </div>
        )}
      </div>

      <Popup 
        isOpen={popup.isOpen} 
        onClose={() => setPopup({ ...popup, isOpen: false })} 
        title={popup.title} 
        message={popup.message} 
        icon={popup.icon}
      />
    </div>
  );
}

export default Orchard;
